/**
 * UID746 · Rekalkulacija knjig. stanja popisa na noviji Faros batch - logika bez Reacta,
 * da se može testirati izravno (tests/js/inventoryDailyRecalc.test.mjs).
 */

export type BatchMeta = { batch_id: number; snapshot_date: string | null; imported_at: string | null; filename: string | null };

export type RecalcHistory = {
    id: number;
    from_batch_id: number;
    to_batch_id: number;
    session_status: string;
    reason: string;
    to_batch: BatchMeta | null;
    items_changed: number;
    total_diff_before_eur: number;
    total_diff_after_eur: number;
    performed_by: number;
    performed_by_name: string | null;
    performed_at: string;
};

export type RecalcOriginal = { original_expected_qty: number; original_batch_id: number; original_diff_qty: number | null };

export type RecalcInfo = {
    frozen_batch_id: number | null;
    frozen_batch: BatchMeta | null;
    original_batch_id: number | null;
    latest_batch_id: number | null;
    newer_batch: boolean;
    items_changed: number;
    status_allows: boolean;
    available: boolean;
    can_recalculate: boolean;
    reason: string | null;
    recalculated: boolean;
    history: RecalcHistory[];
    originals: Record<string, RecalcOriginal>;
};

export type RecalcRow = {
    item_id: number;
    item_code: string;
    item_name: string | null;
    counted_qty: number | null;
    old_expected_qty: number;
    new_expected_qty: number;
    old_unit_price_eur: number | null;
    new_unit_price_eur: number | null;
    old_diff_qty: number | null;
    new_diff_qty: number | null;
    old_diff_value_eur: number | null;
    new_diff_value_eur: number | null;
    old_status: string | null;
    new_status: string | null;
    in_new_batch: boolean;
};

export type RecalcTotals = {
    total_diff_value_eur: number;
    surplus_value_eur: number;
    shortage_value_eur: number;
    items_with_diff: number;
    items_recount: number;
    items_drifted: number;
    items_missing: number;
    items_unknown: number;
};

export type RecalcSummary = {
    from_batch: BatchMeta | null;
    to_batch: BatchMeta | null;
    items_changed: number;
    rows: RecalcRow[];
    totals_before: RecalcTotals;
    totals_after: RecalcTotals;
};

export type RecalcPreview = {
    session_id: number;
    status: string;
    from_batch_id: number | null;
    to_batch_id: number | null;
    executable: boolean;
    reason: string | null;
    summary: RecalcSummary | null;
};

/** Razlog naknadnog uvoza: backend traži najmanje 10 znakova. */
export const MIN_REASON_LENGTH = 10;

export function reasonValid(reason: string): boolean {
    return reason.trim().length >= MIN_REASON_LENGTH;
}

/**
 * Gumb "Rekalkuliraj" se prikazuje samo kad backend kaže da ga ovaj korisnik smije
 * pokrenuti: administrator+, noviji dovršeni batch, promjena knjig. stanja, popis u radu.
 * Za predan/zaključen popis gumba nema, ali se prikazuje napomena zašto (hint).
 */
export function recalcButtonState(info: RecalcInfo | null | undefined): { visible: boolean; hint: string | null } {
    if (!info) return { visible: false, hint: null };
    if (info.can_recalculate) return { visible: true, hint: null };
    if (info.newer_batch && info.items_changed > 0 && !info.status_allows) {
        return { visible: false, hint: info.reason };
    }
    return { visible: false, hint: null };
}

/** Može li se potvrditi: izvršiv pregled, postoji sažetak, razlog dovoljno dug, nije u tijeku. */
export function canConfirm(preview: RecalcPreview | null, reason: string, busy: boolean): boolean {
    return !!preview && preview.executable && !!preview.summary && preview.from_batch_id != null && preview.to_batch_id != null && reasonValid(reason) && !busy;
}

/** Tijelo POST /recalculate - uvijek par batcheva iz pregleda, da backend odbije zastarjeli zahtjev. */
export function confirmBody(preview: RecalcPreview, reason: string): { from_batch_id: number; to_batch_id: number; reason: string } {
    return { from_batch_id: preview.from_batch_id as number, to_batch_id: preview.to_batch_id as number, reason: reason.trim() };
}

const fmt = (n: number | null | undefined): string =>
    // hr-HR daje znak U+2212 za minus; ovdje ide obicni ASCII minus, isti kao u unosu.
    n == null ? '-' : Number(n).toLocaleString('hr-HR', { minimumFractionDigits: 0, maximumFractionDigits: 3 }).replace('\u2212', '-');

/** "28 → 13" */
export function arrow(oldV: number | null | undefined, newV: number | null | undefined): string {
    return `${fmt(oldV)} → ${fmt(newV)}`;
}

/** Posljedica na razlike i ocjenu, za sažetak prije potvrde. */
export function consequence(s: RecalcSummary): { itemsChanged: number; totalBefore: number; totalAfter: number; deltaEur: number; recountBefore: number; recountAfter: number } {
    const before = s.totals_before.total_diff_value_eur;
    const after = s.totals_after.total_diff_value_eur;
    return {
        itemsChanged: s.items_changed,
        totalBefore: before,
        totalAfter: after,
        deltaEur: Math.round((after - before) * 100) / 100,
        recountBefore: s.totals_before.items_recount,
        recountAfter: s.totals_after.items_recount,
    };
}

/** Izvorno knjig. stanje stavke ako ga je rekalkulacija promijenila; inače null. */
export function originalFor(info: RecalcInfo | null | undefined, itemId: number): RecalcOriginal | null {
    if (!info || !info.originals) return null;
    return info.originals[String(itemId)] ?? null;
}

/** Zadnja rekalkulacija (za traku u zaglavlju pregleda). */
export function lastRecalc(info: RecalcInfo | null | undefined): RecalcHistory | null {
    if (!info || !info.recalculated || info.history.length === 0) return null;
    return info.history[info.history.length - 1];
}
